import { gql } from '@apollo/client';
import { HStack, useToast } from '@chakra-ui/react';
import PluginEditor from '@draft-js-plugins/editor';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CommentForm } from '@/components/Post/components/CommentForm';
import { CreateCommentInput_V2 } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';
import { CurrentUserAvatar } from '@/imports/ui/chakra/components/CurrentUserAvatar';

import { CreateReply_CommentFragment } from './graphql-types/CreateReply_CommentFragment';
import { CreateReply_UserFragment } from './graphql-types/CreateReply_UserFragment';

type FormValues = {
  body: EditorState;
  images: ({ imageId: string } | { videoId: string })[];
  attachments: { versionFileId: string; fileName: string }[];
};

type CreateReplyCommentInput = CreateCommentInput_V2;

export const CreateReply = React.forwardRef<
  PluginEditor,
  {
    comment: CreateReply_CommentFragment;
    mentionedUser: CreateReply_UserFragment;
    createReplyComment: (input: CreateReplyCommentInput) => Promise<void>;
    onClickCancel: () => void;
    isSubmitting: boolean;
  }
>(
  (
    { comment, mentionedUser, createReplyComment, onClickCancel, isSubmitting },
    ref
  ) => {
    const toast = useToast();

    const editorRef =
      ref && 'current' in ref && ref.current ? ref.current : undefined;
    React.useEffect(() => {
      editorRef?.focus();
      // @ts-ignore
      editorRef?.editor?.editor?.parentElement?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
    }, [editorRef]);

    const methods = useForm<FormValues>({
      defaultValues: {
        images: [],
        attachments: [],
        body: EditorState.moveSelectionToEnd(
          EditorState.createWithContent(ContentState.createFromText(''))
        ),
      },
    });

    const analyticsClient = useAnalyticsLogger();

    const post = comment.postV2;

    if (!post) {
      return null;
    }

    const createReplyHandler = methods.handleSubmit(async (params) => {
      try {
        await createReplyComment({
          data: {
            postId: post._id,
            commentId: comment._id,
            body: convertToRaw(params.body.getCurrentContent()),
            versionFileIds: params.attachments.map(
              (attachment) => attachment.versionFileId
            ),
            embeddedImageIds: params.images
              .filter((id): id is { imageId: string } => 'imageId' in id)
              .map((id) => id.imageId),
          },
        });
        methods.reset();
        analyticsClient.logEvent_comm_click_comment({
          'added tags': !!post.tags && post.tags.length > 0,
          announcement: !!post.announcement?._id || false,
          'posted in': post.event
            ? 'event'
            : post.organization
            ? 'organization'
            : post.group?.isGlobalSingleton
            ? 'general'
            : 'group',
          'post type':
            post.__typename === 'AnswerV2'
              ? 'answer'
              : post.__typename === 'QuestionV2'
              ? 'question'
              : post.__typename === 'BlogV2'
              ? 'article'
              : post.__typename === 'EventAlert'
              ? 'eventPublished'
              : post.__typename === 'PollV2'
              ? 'poll'
              : 'message',
          'on post': true,
        });
      } catch (e) {
        toast({
          status: 'error',
          description: 'Failed to reply to comment',
        });
        logError(e);
      }
    });

    return (
      <HStack alignItems="start" width="full">
        <CurrentUserAvatar size="sm" />
        <FormProvider {...methods}>
          <CommentForm
            ref={ref}
            onSubmit={createReplyHandler}
            mentionData={{
              _id: mentionedUser._id,
              displayName: mentionedUser.displayName,
              postId: post?._id,
            }}
            submitButtonText="Reply"
            onClickCancel={() => {
              methods.reset();
              onClickCancel();
            }}
            isSubmitting={isSubmitting}
          />
        </FormProvider>
      </HStack>
    );
  }
);
CreateReply.displayName = 'CreateReply';

// Ideally this would simply be CreateReply.commentFragment but ran into tsc problems
// related to CreateReply being a React.forwardRef instead of a React.FC :/
export const CreateReply_CommentFragmentAST = gql`
  fragment CreateReply_CommentFragment on Comment {
    _id
    postV2 {
      _id
      tags {
        _id
      }
      announcement {
        _id
      }
      group {
        _id
        isGlobalSingleton
      }
      organization {
        _id
      }
      event {
        _id
      }
    }
  }
`;

export const CreateReply_UserFragmentAST = gql`
  fragment CreateReply_UserFragment on User {
    _id
    displayName
  }
`;
